import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { Navbar, PageHeader, Button } from '../components';
import LoadingSpinner from '../components/LoadingSpinner';
import notificationPreferenceService from '../services/notificationPreferenceService';
import useUserLocation from '../hooks/useUserLocation';

/**
 * Groups the 60-ish NotificationType strings into UX-friendly buckets.
 * Each entry is { label, types: [{type, label, description}] }. Anything
 * not listed falls into 'Other' automatically — so adding a new
 * NotificationType server-side never breaks this page, it just shows up
 * under Other until we curate it here.
 */
const GROUPS = [
  {
    label: 'Chat & Connections',
    items: [
      { type: 'MessageReceived', label: 'New message', description: 'Someone sent you a chat message' },
      { type: 'FriendRequestReceived', label: 'Friend request received' },
      { type: 'FriendRequestAccepted', label: 'Friend request accepted' },
      { type: 'IntroductionReceived', label: 'Introduction received', description: 'Someone wants to introduce you to a friend' },
      { type: 'IntroductionCompleted', label: 'Introduction completed' },
      { type: 'IntroductionDeclined', label: 'Introduction declined' },
      { type: 'CloseFriendAdded', label: 'Added to close friends', description: 'A friend added you to their close friends' },
    ],
  },
  {
    label: 'Events (as guest)',
    items: [
      { type: 'ApplicationAccepted', label: 'You\'re in' },
      { type: 'ApplicationDeclined', label: 'You\'re declined' },
      { type: 'EventInviteReceived', label: 'Event invite received' },
      { type: 'EventGreenlit', label: 'Event greenlit' },
      { type: 'EventCanceled', label: 'Event canceled' },
      { type: 'EventEnded', label: 'Event ended' },
      { type: 'EventAnnouncementPosted', label: 'Event announcement posted' },
      { type: 'WaitlistSpotAvailable', label: 'Waitlist spot available' },
      { type: 'MimeRequestReceived', label: 'MIME request received' },
      { type: 'MimeRequestAccepted', label: 'MIME request accepted' },
      { type: 'MimeRequestDeclined', label: 'MIME request declined' },
      { type: 'TicketPurchased', label: 'Ticket purchased' },
      { type: 'PartyPatronGiftReceived', label: 'Party Patron gift received' },
      { type: 'EventSponsorGiftReceived', label: 'Event sponsor gift received' },
    ],
  },
  {
    label: 'Events (as host)',
    items: [
      { type: 'ApplicationReceived', label: 'New application' },
      { type: 'ApplicationWithdrawn', label: 'Application withdrawn' },
      { type: 'PaymentReceived', label: 'Payment received' },
      { type: 'GuestCheckoutReceived', label: 'Guest checkout received' },
      { type: 'WaitlistJoined', label: 'Someone joined waitlist' },
      { type: 'PartyTrickProposed', label: 'Party trick proposed' },
      { type: 'PartyTrickReviewed', label: 'Party trick reviewed' },
      { type: 'VerificationSubmitted', label: 'Verification submitted' },
      { type: 'FeedbackReceived', label: 'Feedback received' },
      { type: 'EscrowReleased', label: 'Escrow released' },
      { type: 'EventFeatured', label: 'Event featured' },
      { type: 'BrandConversionReport', label: 'Brand conversion report' },
      { type: 'PaymentReminderSent', label: 'Payment reminder sent' },
    ],
  },
  {
    label: 'Wallet & Payments',
    items: [
      { type: 'WalletTopUpCompleted', label: 'Top-up completed' },
      { type: 'CommitmentRefunded', label: 'Commitment refunded' },
      { type: 'WithdrawalCompleted', label: 'Cash-out completed' },
      { type: 'WithdrawalFailed', label: 'Cash-out failed' },
      { type: 'PeerTransferReceived', label: 'Peer transfer received' },
      { type: 'IdentityVerified', label: 'Identity verified' },
    ],
  },
  {
    label: 'Clubs',
    items: [
      { type: 'ClubInvite', label: 'Club invite' },
      { type: 'ClubAnnouncementNew', label: 'Club announcement' },
      { type: 'ClubMemberJoined', label: 'New club member' },
      { type: 'ClubMemberRemoved', label: 'Member removed from club' },
      { type: 'ClubPollCreated', label: 'New club poll' },
      { type: 'ClubJoinRequestReceived', label: 'Join request received' },
      { type: 'ClubJoinRequestAccepted', label: 'Join request accepted' },
      { type: 'ClubJoinRequestDeclined', label: 'Join request declined' },
      { type: 'ClubJoinRequestBlocked', label: 'Join request blocked' },
      { type: 'ClubMembershipPurchased', label: 'Membership purchased' },
      { type: 'ClubMembershipRenewed', label: 'Membership renewed' },
      { type: 'ClubMembershipRenewalFailed', label: 'Membership renewal failed' },
      { type: 'ClubMembershipExpiring', label: 'Membership expiring soon' },
      { type: 'ClubMembershipExpired', label: 'Membership expired' },
      { type: 'ClubMembershipRefunded', label: 'Membership refunded' },
      { type: 'ClubMembershipCanceled', label: 'Membership canceled' },
    ],
  },
  {
    label: 'Portal',
    items: [
      { type: 'PortalApplicationAccepted', label: 'Application accepted' },
      { type: 'PortalApplicationDeclined', label: 'Application declined' },
      { type: 'PortalApplicationNotYet', label: 'Application — not yet' },
      { type: 'PortalInviteUsed', label: 'Your invite was used' },
      { type: 'PortalAdminPromoted', label: 'Promoted to Portal admin' },
      { type: 'PortalAdminDemoted', label: 'Removed as Portal admin' },
      { type: 'PortalMembershipRemoved', label: 'Removed from Portal' },
      { type: 'PortalSignalReceived', label: 'New Portal Signal' },
      { type: 'PortalSignalInvited', label: 'Invited to Portal Signal' },
      { type: 'PortalSignalInviteDeclined', label: 'Signal invite declined' },
      { type: 'PortalSignalReplied', label: 'Signal reply received' },
      { type: 'PortalSignalApplicationReceived', label: 'Signal application received' },
      { type: 'PortalSignalAccepted', label: 'Signal application accepted' },
    ],
  },
];

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [items, setItems] = useState([]); // [{type, pushEnabled, emailEnabled}]
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // `${type}:${channel}` while in-flight

  // Event digest — its own scheduled send, not a NotificationType.
  const [digest, setDigest] = useState(null); // {cadence, lat, lng, city, radiusMiles, ...}
  // The digest covers wherever the person is: the same pill as the wall. Its coordinates ride
  // the save as the server's fallback for someone the tracker has never placed.
  const here = useUserLocation();
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestError, setDigestError] = useState(null);

  // Broadcast/newsletter opt-out — marketing only, never transactional mail.
  const [marketing, setMarketing] = useState(true);
  const [marketingSaving, setMarketingSaving] = useState(false);

  // Mutes (Phase 3c)
  const [mutes, setMutes] = useState([]);
  useEffect(() => {
    let done = false;
    import('../services/notificationService').then(({ default: ns }) => ns.getMutes()).then((r) => {
      if (!done && r.success) setMutes(r.data || []);
    });
    return () => { done = true; };
  }, []);
  const unmute = async (m) => {
    const { default: ns } = await import('../services/notificationService');
    const r = await ns.removeMute(m.targetType, m.targetId);
    if (r.success) setMutes((prev) => prev.filter((x) => !(x.targetType === m.targetType && x.targetId === m.targetId)));
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [prefs, dig] = await Promise.all([
        notificationPreferenceService.list(),
        notificationPreferenceService.getDigest(),
      ]);
      if (prefs?.success) {
        setItems(prefs.data?.items || []);
        setMarketing(prefs.data?.marketingEmailsEnabled !== false);
      }
      if (dig?.success) setDigest(dig.data);
      setLoading(false);
    })();
  }, []);

  const saveDigest = async (cadence) => {
    const prev = digest;
    setDigestError(null);
    setDigestSaving(true);
    setDigest(d => ({ ...d, cadence }));
    const point = here.lat != null && here.lng != null ? { lat: here.lat, lng: here.lng } : null;
    const r = await notificationPreferenceService.updateDigest(cadence, point);
    if (!r?.success) {
      setDigest(prev);
      setDigestError(r?.message || 'Could not save. Try again.');
    }
    setDigestSaving(false);
  };

  const byType = useMemo(() => {
    const m = {};
    for (const i of items) m[i.type] = i;
    return m;
  }, [items]);

  // Build the "Other" bucket from any type the server returned that we
  // didn't explicitly catalog above — keeps the page future-proof.
  const known = useMemo(() => new Set(GROUPS.flatMap(g => g.items.map(i => i.type))), []);
  const otherTypes = useMemo(
    () => items.map(i => i.type).filter(t => !known.has(t)),
    [items, known]
  );
  const groupsWithOther = useMemo(() => {
    if (otherTypes.length === 0) return GROUPS;
    return [
      ...GROUPS,
      { label: 'Other', items: otherTypes.map(t => ({ type: t, label: humanize(t) })) },
    ];
  }, [otherTypes]);

  const toggle = async (type, channel) => {
    const current = byType[type];
    const wasEnabled = channel === 'push' ? current?.pushEnabled : current?.emailEnabled;
    const next = !wasEnabled;
    const key = `${type}:${channel}`;
    setSavingKey(key);
    // Optimistic update.
    setItems(prev => prev.map(i => i.type === type
      ? { ...i, [channel === 'push' ? 'pushEnabled' : 'emailEnabled']: next }
      : i));
    const r = await notificationPreferenceService.update(type, channel, next);
    if (!r?.success) {
      // Revert.
      setItems(prev => prev.map(i => i.type === type
        ? { ...i, [channel === 'push' ? 'pushEnabled' : 'emailEnabled']: wasEnabled }
        : i));
    }
    setSavingKey(null);
  };

  const toggleMarketing = async () => {
    const next = !marketing;
    setMarketingSaving(true);
    setMarketing(next); // optimistic
    const r = await notificationPreferenceService.updateMarketing(next);
    if (!r?.success) setMarketing(!next);
    setMarketingSaving(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate(profile?.initialMode === 'host' ? '/host' : '/guest')}
      />

      <PageHeader
        title="NOTIFICATION SETTINGS"
        subtitle="Choose what gets pushed to your phone and what shows up in your email"
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate('/notifications')}>
            BACK
          </Button>
        }
      />

      <div className="max-w-3xl mx-auto">
        {loading ? (
          <div className="px-6 py-16 text-center">
            <LoadingSpinner label="LOADING..." />
          </div>
        ) : (
          <div>
            <DigestSection
              digest={digest}
              town={here.city}
              saving={digestSaving}
              error={digestError}
              onSave={saveDigest}
            />

            <section className="border-b-2 border-black">
              <h2 className="px-6 pt-6 pb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black border-b-2 border-black bg-gray-50">
                Announcements
              </h2>
              <PreferenceRow
                label="Portal newsletter"
                description="Occasional broadcasts from us. Never receipts, tickets or event updates — those always come through."
                emailEnabled={marketing}
                onToggleEmail={toggleMarketing}
                savingEmail={marketingSaving}
              />
            </section>

            {/* Muted people + rooms (Phase 3c) */}
            {mutes.length > 0 && (
              <section className="border-b-2 border-black">
                <h2 className="px-6 pt-6 pb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black border-b-2 border-black bg-gray-50">
                  Muted
                </h2>
                {mutes.map((m) => (
                  <div key={`${m.targetType}-${m.targetId}`} className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-3">
                    <div className="min-w-0">
                      <span className="font-mono text-sm font-bold text-black">{m.label}</span>
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-wide text-gray-400">{m.targetType === 'club' ? 'room' : 'person'}</span>
                    </div>
                    <button type="button" onClick={() => unmute(m)}
                      className="border-2 border-black px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white">
                      Unmute
                    </button>
                  </div>
                ))}
              </section>
            )}

            {/* Column header row */}
            <div className="px-6 py-3 border-b-2 border-black flex items-center justify-between gap-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                In-app feed is always on
              </div>
              <div className="flex items-center gap-6">
                <div className="font-mono text-[10px] font-bold uppercase tracking-widest w-12 text-center">Push</div>
                <div className="font-mono text-[10px] font-bold uppercase tracking-widest w-12 text-center">Email</div>
              </div>
            </div>

            {groupsWithOther.map(group => (
              <section key={group.label}>
                <h2 className="px-6 pt-6 pb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black border-b-2 border-black bg-gray-50">
                  {group.label}
                </h2>
                <div className="divide-y divide-gray-200 border-b-2 border-black">
                  {group.items.map(item => {
                    const pref = byType[item.type] || { pushEnabled: true, emailEnabled: true };
                    return (
                      <PreferenceRow
                        key={item.type}
                        label={item.label}
                        description={item.description}
                        pushEnabled={pref.pushEnabled}
                        emailEnabled={pref.emailEnabled}
                        onTogglePush={() => toggle(item.type, 'push')}
                        onToggleEmail={() => toggle(item.type, 'email')}
                        savingPush={savingKey === `${item.type}:push`}
                        savingEmail={savingKey === `${item.type}:email`}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The digest's area is never picked here: it is the Featured wall's 50-mile ring around
 * wherever the person is (the location pill, which rides every tracked page view), closest
 * first, a featured event in their town leading. There used to be a region dropdown whose
 * default put people in a village in Iran.
 */
function DigestSection({ digest, town, saving, error, onSave }) {
  if (!digest) return null;

  const cadence = digest.cadence || 'Off';
  const isOn = cadence !== 'Off';
  const where = (digest.city || town || '').trim();
  const miles = digest.radiusMiles || 50;

  const OPTIONS = [
    { value: 'Off', label: 'OFF', note: 'No digest' },
    { value: 'Daily', label: 'DAILY', note: 'Every morning' },
    { value: 'Weekly', label: 'WEEKLY', note: 'Every week' },
    { value: 'Biweekly', label: 'BIWEEKLY', note: 'Every two weeks' },
    { value: 'Monthly', label: 'MONTHLY', note: 'Every month' },
  ];

  const pick = (next) => {
    if (next === cadence) return;
    onSave(next);
  };

  return (
    <section className="border-b-2 border-black">
      <h2 className="px-6 pt-6 pb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black border-b-2 border-black bg-gray-50">
        Event digest
      </h2>

      <div className="px-6 py-4">
        <div className="font-mono text-sm text-black">A roundup of events near you</div>
        <div className="font-mono text-[10px] text-gray-500 mt-1">
          Weekly by default. Pick how often you want it — we skip the send entirely when
          there's nothing on.
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {OPTIONS.map(o => {
            const active = o.value === cadence;
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={active}
                disabled={saving}
                onClick={() => pick(o.value)}
                title={o.note}
                className={`border-2 border-black px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  active ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {isOn && (
          <div className="mt-4" data-testid="digest-area">
            <div className="block font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
              Area
            </div>
            <div className="font-mono text-xs uppercase tracking-wider text-black">
              {where ? `WITHIN ${miles} MILES OF ${where}` : `WITHIN ${miles} MILES OF WHERE YOU USE PORTAL`}
            </div>
            <div className="font-mono text-[10px] text-gray-500 mt-1">
              Closest first, featured events in your town at the top. It follows the town in the
              location pill at the top of the page — change it there.
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 font-mono text-[10px] text-red-600">{error}</div>
        )}
      </div>
    </section>
  );
}

/**
 * One row of the matrix. `onTogglePush` is optional — the newsletter row is
 * email-only, and rendering a dead push switch beside it would imply a channel
 * that doesn't exist. The empty cell keeps the two columns aligned.
 */
function PreferenceRow({ label, description, pushEnabled, emailEnabled, onTogglePush, onToggleEmail, savingPush, savingEmail }) {
  return (
    <div className="px-6 py-3 flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="font-mono text-sm font-medium text-black">{label}</div>
        {description && (
          <div className="font-mono text-[10px] text-gray-500 mt-0.5">{description}</div>
        )}
      </div>
      <div className="flex items-center gap-6 flex-shrink-0">
        {onTogglePush ? (
          <Switch on={pushEnabled} onChange={onTogglePush} busy={savingPush} ariaLabel={`Push for ${label}`} />
        ) : (
          <div className="w-11" aria-hidden="true" />
        )}
        <Switch on={emailEnabled} onChange={onToggleEmail} busy={savingEmail} ariaLabel={`Email for ${label}`} />
      </div>
    </div>
  );
}

/**
 * iOS-style toggle switch — green when on, red when off. Black border keeps
 * it on-brand with the rest of the brutalist palette. Disabled while a
 * request is in flight to prevent double-clicks racing the optimistic update.
 */
function Switch({ on, onChange, busy, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      onClick={onChange}
      disabled={busy}
      className={`relative inline-flex h-6 w-11 items-center border-2 border-black transition-colors disabled:opacity-50 ${
        on ? 'bg-green-500' : 'bg-red-500'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform border-2 border-black bg-white transition-transform ${
          on ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function humanize(camelOrPascal) {
  return camelOrPascal
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}
